// src/pages/Agents/AIAgentCreation.tsx — create an AI agent.
//
// Shaped around what the agent will *do*, not the engine that runs it:
// template -> knowledge -> voice -> capabilities -> instructions -> review.
// Engine settings (LLM, STT/TTS, conversation tuning) are all still here, but
// behind "Advanced" disclosures on the step they belong to — nothing the old
// tabbed form could set has been dropped.
//
// Capabilities are real AgentTools: on submit the agent is created first, then
// each enabled capability is upserted against it. A tool that needs an endpoint
// is flagged in the UI rather than silently created half-wired.
import React, { useEffect, useMemo, useState } from 'react';
import { BusinessSwitcher } from 'components/Common/BusinessSwitcher';
import {
  Bot, ArrowLeft, ArrowRight, Check, Mic, BookOpen,
  Wrench, FileText, Sparkles, AlertTriangle, ChevronDown,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { aiAgentsService, agentToolsService, agentSettingsService, AgentTool } from 'api/services/aiAgents.service';
import { memoryProfilesService, MemoryProfile } from 'api/services/memoryProfiles.service';
import { agentCatalogService, AgentCatalog, CatalogOption } from 'api/services/agentCatalog.service';
import {
  CAPABILITIES, TEMPLATES, TONES, LANGUAGES, voicesFor,
} from './agentWizardCatalog';
import {
  Modality, WizardState, buildCreatePayload,
  REALTIME_MODEL, REALTIME_PROVIDER, DEFAULT_MODEL, DEFAULT_PROVIDER,
} from './agentCreatePayload';

type StepId = 'template' | 'knowledge' | 'voice' | 'capabilities' | 'instructions' | 'review';

const initialCapabilities = () =>
  CAPABILITIES.reduce<Record<string, boolean>>((acc, c) => ({ ...acc, [c.id]: !!c.defaultOn }), {});

const AIAgentCreation: React.FC = () => {
  const navigate = useNavigate();
  /**
   * The business this agent belongs to.
   *
   * Without it the backend falls back to the organisation on the session — the
   * one you LOGGED IN to — so a service-provider admin creating an agent for a
   * child business silently created it on the provider instead, where the
   * business's own Agents list could never show it.
   */
  const [orgId, setOrgId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [memories, setMemories] = useState<MemoryProfile[]>([]);
  const [developerMode, setDeveloperMode] = useState(false);
  const [customTools, setCustomTools] = useState<AgentTool[]>([]);
  const [toolDraft, setToolDraft] = useState<{ name: string; description: string; execution_type: string; paramsText: string } | null>(null);
  const [stepIdx, setStepIdx] = useState(0);
  const [showAdvanced, setShowAdvanced] = useState<Record<string, boolean>>({});
  // The same capability catalog the editor's dropdowns use (seed + values harvested
  // from real agents). Null until loaded; every use falls back to hardcoded defaults
  // so the wizard works even if the catalog call fails.
  const [catalog, setCatalog] = useState<AgentCatalog | null>(null);

  const [s, setS] = useState<WizardState>({
    templateId: '',
    modality: 'voice_pipeline',
    name: '',
    description: '',
    knowledgeBase: '',
    enableKnowledgeBase: false,
    kbTopK: 3,
    kbThreshold: 0.45,
    language: 'en-IN',
    extraLanguages: [],
    voice: 'aaravi',
    tone: 'Warm',
    sttProvider: '',
    sttModel: '',
    ttsProvider: '',
    ttsVoiceId: '',
    ttsModel: '',
    capabilities: initialCapabilities(),
    greetingMessage: 'Hello! How can I assist you today?',
    systemPrompt: '',
    memoryProfileId: '',
    provider: 'openai',
    model: 'gpt-4o',
    temperature: 0.7,
    maxTokens: 150,
    agentFlowType: 'streaming',
    hangupAfterSilence: '',
    callTerminate: '',
    agentSpeaksFirst: true,
    checkIfUserOnline: '',
    checkUserOnlineMessage: '',
    triggerUserOnlineMessageAfter: '',
    numberOfWordsForInterruption: '',
    backchanneling: '',
    ambientNoise: '',
    ambientNoiseTrack: '',
    conversationConfigJson: '',
  });

  const set = <K extends keyof WizardState>(k: K, v: WizardState[K]) => setS(p => ({ ...p, [k]: v }));

  useEffect(() => {
    memoryProfilesService.list().then(r => { if (r.success) setMemories(r.data || []); });
    agentSettingsService.getDeveloperMode().then(r => { if (r.success) setDeveloperMode(!!r.data?.developerMode); });
    agentCatalogService.get().then(r => { if (r.success) setCatalog(r.data || null); });
  }, []);

  const isVoice = s.modality !== 'text';
  const isPipeline = s.modality === 'voice_pipeline';
  const isRealtime = s.modality === 'voice_realtime';

  // ── Catalog-driven option lists (fall back to hardcoded defaults when empty) ──
  // A small helper: dedupe options by value, always keeping the currently-selected
  // value present so a saved-but-unlisted choice never disappears.
  const withCurrent = (opts: { value: string; label: string }[], current: string) =>
    (current && !opts.some(o => o.value === current))
      ? [{ value: current, label: current }, ...opts]
      : opts;

  const llmProviderOptions = useMemo(() => {
    const base = [
      { value: 'openai', label: 'OpenAI' },
      { value: 'gemini', label: 'Gemini (Google)' },
      { value: 'claude', label: 'Claude (Anthropic)' },
    ];
    const cat = (catalog?.LLM_PROVIDER || []).map(o => ({ value: o.value, label: o.label }));
    const seen = new Set<string>();
    const merged = [...base, ...cat].filter(o => !seen.has(o.value) && seen.add(o.value));
    return withCurrent(merged, s.provider);
  }, [catalog, s.provider]);

  const llmModelOptions = useMemo(() => {
    const cat = (catalog?.LLM_MODEL || [])
      .filter(o => !o.provider || o.provider === s.provider)
      .map(o => ({ value: o.value, label: o.label }));
    return withCurrent(cat, s.model);
  }, [catalog, s.provider, s.model]);

  // Catalog TTS voices as wizard voice-cards (pipeline only — realtime keeps its own
  // gemini voice list). label carries the human name; provider becomes the style hint.
  const catalogVoiceCards = useMemo(() => {
    return (catalog?.TTS_VOICE || []).map((o: CatalogOption) => ({
      id: o.value, name: o.label || o.value, gender: '', style: o.provider || 'catalog',
    }));
  }, [catalog]);

  // The voice cards shown: realtime keeps its own gemini list; a pipeline agent gets
  // the hardcoded voices PLUS every real voice from the catalog (deduped by id).
  const voiceCards = useMemo(() => {
    const base = voicesFor(isRealtime);
    if (isRealtime) return base;
    const seen = new Set(base.map(v => v.id));
    return [...base, ...catalogVoiceCards.filter(v => !seen.has(v.id))];
  }, [isRealtime, catalogVoiceCards]);

  /**
   * Change the runtime type, carrying the engine defaults that belong to it.
   *
   * A realtime agent is served by Gemini Live, which reads llm_config.model and
   * llm_config.voice_name: leaving the pipeline defaults (gpt-4o, 'aaravi')
   * would give a correctly-shaped realtime document pointed at a model that
   * cannot serve it, speaking a voice that does not exist. Anything the user
   * typed themselves is left alone — only untouched defaults are swapped.
   */
  const setModality = (m: Modality) => setS(prev => {
    const next: WizardState = { ...prev, modality: m };
    const wantsRealtime = m === 'voice_realtime';
    const untouchedEngine =
      (prev.provider === DEFAULT_PROVIDER && prev.model === DEFAULT_MODEL) ||
      (prev.provider === REALTIME_PROVIDER && prev.model === REALTIME_MODEL);
    if (untouchedEngine) {
      next.provider = wantsRealtime ? REALTIME_PROVIDER : DEFAULT_PROVIDER;
      next.model = wantsRealtime ? REALTIME_MODEL : DEFAULT_MODEL;
    }
    // The two voice lists share no ids, so a voice from the other mode would
    // be sent as a name the runtime has never heard of.
    if (!voicesFor(wantsRealtime).some(v => v.id === prev.voice)) {
      next.voice = voicesFor(wantsRealtime)[0].id;
    }
    return next;
  });

  // Text agents have no voice step at all.
  const steps: { id: StepId; label: string; icon: any }[] = useMemo(() => ([
    { id: 'template', label: 'Template', icon: Sparkles },
    { id: 'knowledge', label: 'Knowledge', icon: BookOpen },
    ...(isVoice ? [{ id: 'voice' as StepId, label: 'Voice & language', icon: Mic }] : []),
    { id: 'capabilities', label: 'Capabilities', icon: Wrench },
    { id: 'instructions', label: 'Instructions', icon: FileText },
    { id: 'review', label: 'Review & activate', icon: Check },
  ]), [isVoice]);

  const step = steps[Math.min(stepIdx, steps.length - 1)];
  const isLast = stepIdx >= steps.length - 1;

  const applyTemplate = (id: string) => {
    const t = TEMPLATES.find(x => x.id === id);
    if (!t) return;
    setS(prev => ({
      ...prev,
      templateId: id,
      greetingMessage: t.greeting,
      systemPrompt: t.systemPrompt,
      name: prev.name || (t.id === 'blank' ? '' : t.label),
      capabilities: CAPABILITIES.reduce<Record<string, boolean>>(
        (acc, c) => ({ ...acc, [c.id]: t.capabilities.includes(c.id) }), {}),
    }));
  };

  const template = TEMPLATES.find(t => t.id === s.templateId);
  const enabledCaps = CAPABILITIES.filter(c => s.capabilities[c.id]);
  const needsWiring = enabledCaps.filter(c => c.needsWiring);

  // Step 1 needs a name before anything else is meaningful.
  const canAdvance = step.id === 'template' ? !!s.name.trim() && !!s.templateId : true;

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    setWarning(null);
    try {
      let conversationConfig: Record<string, any> | undefined;
      if (s.conversationConfigJson.trim()) {
        try { conversationConfig = JSON.parse(s.conversationConfigJson); }
        catch { setError('Conversation config override is not valid JSON'); setLoading(false); return; }
      }

      const res = await aiAgentsService.create(buildCreatePayload(s, conversationConfig), orgId || undefined);

      if (!res.success || !res.data) {
        setError(res.error || 'Failed to create agent');
        setLoading(false);
        return;
      }

      // The agent exists now. Capabilities are written as tools against it —
      // report partial failure rather than pretending the whole thing worked.
      const agentId = res.data.id;
      const failed: string[] = [];
      if (agentId) {
        for (const cap of enabledCaps) {
          const r = await agentToolsService.upsert(agentId, { ...cap.tool });
          if (!r.success) failed.push(cap.label);
        }
        // developer-mode custom JSON tools
        for (const ct of customTools) {
          const r = await agentToolsService.upsert(agentId, { ...ct });
          if (!r.success) failed.push(ct.name);
        }
      }

      if (failed.length) {
        setWarning(
          `Agent created, but these capabilities could not be saved: ${failed.join(', ')}. ` +
          'Add them from the agent editor.'
        );
        setLoading(false);
        return;
      }

      // Flow straight into full configuration on the new agent. Fall back to the list
      // if the create response somehow carried no id, so it never dead-ends.
      if (agentId) navigate(`/agents/${agentId}/config`);
      else navigate('/agents');
    } catch (err: any) {
      setError(err?.message || 'Failed to create agent');
      setLoading(false);
    }
  };

  // ── shared field styles ──
  const input = 'w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 rounded-xl focus:ring-2 focus:ring-blue-400 focus:border-transparent';
  const lbl = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2';
  const h3 = 'text-[15px] font-semibold text-gray-900 dark:text-white mb-3';

  const Advanced: React.FC<{ id: string; label: string; children: React.ReactNode }> = ({ id, label, children }) => (
    <div className="mt-6 border-t pt-4" style={{ borderColor: 'var(--divider)' }}>
      <button
        type="button"
        onClick={() => setShowAdvanced(p => ({ ...p, [id]: !p[id] }))}
        className="flex items-center gap-2 text-sm font-medium"
        style={{ color: 'var(--text-secondary)' }}
      >
        <ChevronDown className={`w-4 h-4 transition-transform ${showAdvanced[id] ? '' : '-rotate-90'}`} />
        {label}
      </button>
      {showAdvanced[id] && <div className="mt-4">{children}</div>}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="tl-icon-btn">
          <ArrowLeft className="w-[18px] h-[18px]" />
        </button>
        <div className="flex items-center gap-2">
          <Bot className="w-5 h-5" style={{ color: 'var(--accent)' }} />
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">New agent</h1>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-gray-500 dark:text-gray-400">Business</span>
          <BusinessSwitcher value={orgId} onChange={setOrgId} />
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-3 py-2">{error}</div>
      )}
      {warning && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-xl px-3 py-2 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            {warning}
            <button onClick={() => navigate('/agents')} className="ml-2 underline font-medium">Go to agents</button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[210px_1fr_290px] gap-5 items-start">
        {/* Step rail */}
        <div className="flex lg:flex-col gap-1 overflow-x-auto">
          {steps.map((st, i) => {
            const cur = i === stepIdx;
            const done = i < stepIdx;
            return (
              <button
                key={st.id}
                onClick={() => setStepIdx(i)}
                className={`tl-nav-item ${cur ? 'tl-nav-item-active' : ''} whitespace-nowrap`}
              >
                <span
                  className="w-[22px] h-[22px] rounded-full flex items-center justify-center text-[11px] font-semibold flex-shrink-0"
                  style={{
                    background: done ? 'var(--toggle-on)' : cur ? 'var(--accent)' : 'var(--divider)',
                    color: done || cur ? '#fff' : 'var(--text-muted)',
                  }}
                >
                  {done ? <Check className="w-3 h-3" /> : i + 1}
                </span>
                <span className="flex-1 text-left">{st.label}</span>
              </button>
            );
          })}
        </div>

        {/* Step body */}
        <div className="tl-card" style={{ minHeight: 380 }}>
          {step.id === 'template' && (
            <div>
              <h3 className={h3}>Pick a template</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {TEMPLATES.map(t => {
                  const sel = s.templateId === t.id;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => applyTemplate(t.id)}
                      className="text-left p-3.5 rounded-xl border transition"
                      style={{
                        borderColor: sel ? 'var(--accent)' : 'var(--border)',
                        background: sel ? 'var(--coral-tint)' : 'var(--surface)',
                      }}
                    >
                      <div className="text-[13.5px] font-semibold" style={{ color: sel ? 'var(--coral-value)' : 'var(--text-primary)' }}>
                        {t.label}
                      </div>
                      <div className="text-[11.5px] mt-1" style={{ color: sel ? 'var(--coral-label)' : 'var(--text-muted)' }}>
                        {t.blurb}
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="mt-6">
                <label className={lbl}>Agent name <span className="text-red-500">*</span></label>
                <input type="text" value={s.name} onChange={e => set('name', e.target.value)} className={input} placeholder="e.g. Riya — Receptionist" />
              </div>
              <div className="mt-4">
                <label className={lbl}>Description</label>
                <input type="text" value={s.description} onChange={e => set('description', e.target.value)} className={input} placeholder="What this agent is for" />
              </div>

              <Advanced id="modality" label="Runtime type">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {([
                    { v: 'voice_pipeline', t: 'Voice — Pipeline', d: 'STT → LLM → TTS. Pick your transcriber & synthesizer.' },
                    { v: 'voice_realtime', t: 'Voice — Realtime', d: 'Speech-to-speech model. No separate STT/TTS.' },
                    { v: 'text', t: 'Text / Messaging', d: 'Chat, WhatsApp, RCS, email. No voice config.' },
                  ] as const).map(o => (
                    <button
                      type="button"
                      key={o.v}
                      onClick={() => { setModality(o.v); setStepIdx(0); }}
                      className="text-left p-3 rounded-xl border transition"
                      style={{
                        borderColor: s.modality === o.v ? 'var(--accent)' : 'var(--border)',
                        background: s.modality === o.v ? 'var(--coral-tint)' : 'var(--surface)',
                      }}
                    >
                      <div className="text-sm font-medium text-gray-900 dark:text-white">{o.t}</div>
                      <div className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{o.d}</div>
                    </button>
                  ))}
                </div>
                <p className="text-xs mt-2" style={{ color: 'var(--text-muted)' }}>
                  Channels (voice / chat / whatsapp / rcs / email) are bound after creating, in the agent editor.
                </p>
              </Advanced>
            </div>
          )}

          {step.id === 'knowledge' && (
            <div>
              <h3 className={h3}>Business knowledge</h3>
              <p className="text-xs mb-3" style={{ color: 'var(--text-muted)' }}>
                What the agent should know to answer questions. This becomes the agent's context.
              </p>
              <textarea
                value={s.knowledgeBase}
                onChange={e => set('knowledgeBase', e.target.value)}
                rows={12}
                className={input}
                placeholder={template?.knowledgePlaceholder || 'Services and prices, hours, policies, directions.'}
              />
              <label className="inline-flex items-center gap-2 text-sm mt-4 text-gray-700 dark:text-gray-300">
                <input type="checkbox" checked={s.enableKnowledgeBase} onChange={e => set('enableKnowledgeBase', e.target.checked)} />
                Enable document RAG — upload documents after creating
              </label>

              <Advanced id="kb" label="Retrieval settings">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={lbl}>top_k</label>
                    <input type="number" min={1} max={10} value={s.kbTopK} onChange={e => set('kbTopK', parseInt(e.target.value))} className={input} />
                  </div>
                  <div>
                    <label className={lbl}>Similarity threshold</label>
                    <input type="number" step={0.05} min={0} max={1} value={s.kbThreshold} onChange={e => set('kbThreshold', parseFloat(e.target.value))} className={input} />
                  </div>
                </div>
              </Advanced>
            </div>
          )}

          {step.id === 'voice' && (
            <div>
              <h3 className={h3}>Voice &amp; language</h3>

              <div className="text-xs font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>Primary language</div>
              <div className="flex gap-1.5 flex-wrap mb-5">
                {LANGUAGES.map(l => {
                  const sel = s.language === l.code;
                  return (
                    <button
                      key={l.code}
                      type="button"
                      onClick={() => set('language', l.code)}
                      className="px-3 py-1.5 rounded-full border text-[12.5px] transition"
                      style={{
                        borderColor: sel ? 'var(--accent)' : 'var(--border)',
                        background: sel ? 'var(--coral-tint)' : 'var(--surface)',
                        color: sel ? 'var(--coral-value)' : 'var(--text-secondary)',
                      }}
                    >
                      {l.label}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs mb-5" style={{ color: 'var(--text-muted)' }}>
                Additional languages get their own prompt &amp; greeting — add them in the agent editor after creating.
              </p>

              <div className="text-xs font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>Voice</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-5">
                {voiceCards.map(v => {
                  const sel = s.voice === v.id;
                  return (
                    <button
                      key={v.id}
                      type="button"
                      onClick={() => set('voice', v.id)}
                      className="p-3 rounded-xl border text-left transition"
                      style={{
                        borderColor: sel ? 'var(--accent)' : 'var(--border)',
                        background: sel ? 'var(--coral-tint)' : 'var(--surface)',
                      }}
                    >
                      <div className="text-[13px] font-semibold text-gray-900 dark:text-white">{v.name}</div>
                      <div className="text-[11px]" style={{ color: 'var(--text-muted)' }}>{v.gender} · {v.style}</div>
                    </button>
                  );
                })}
              </div>

              <div className="text-xs font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>Tone</div>
              <div className="flex gap-1.5 flex-wrap">
                {TONES.map(t => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => set('tone', t)}
                    className={`tl-pill ${s.tone === t ? 'tl-pill-coral' : ''}`}
                    style={{ cursor: 'pointer', padding: '6px 12px', fontSize: 12.5 }}
                  >
                    {t}
                  </button>
                ))}
              </div>

              <Advanced id="engine" label="Speech engine">
                {isPipeline ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div><label className={lbl}>STT provider</label>
                      <input type="text" value={s.sttProvider} onChange={e => set('sttProvider', e.target.value)} className={input} placeholder="deepgram (default)" /></div>
                    <div><label className={lbl}>STT model</label>
                      <input type="text" value={s.sttModel} onChange={e => set('sttModel', e.target.value)} className={input} placeholder="nova-2 (default)" /></div>
                    <div><label className={lbl}>TTS provider</label>
                      <input type="text" value={s.ttsProvider} onChange={e => set('ttsProvider', e.target.value)} className={input} placeholder="elevenlabs (default)" /></div>
                    <div><label className={lbl}>TTS voice id</label>
                      <input type="text" value={s.ttsVoiceId} onChange={e => set('ttsVoiceId', e.target.value)} className={input} placeholder="provider voice id" /></div>
                    <div><label className={lbl}>TTS model</label>
                      <input type="text" value={s.ttsModel} onChange={e => set('ttsModel', e.target.value)} className={input} placeholder="eleven_multilingual_v2" /></div>
                  </div>
                ) : (
                  <p className="text-xs px-3 py-2 rounded-xl" style={{ color: 'var(--text-muted)', background: 'var(--surface-raised)' }}>
                    Realtime mode: the speech-to-speech model handles transcription &amp; synthesis — only the voice and language above apply.
                  </p>
                )}
              </Advanced>

              <Advanced id="conversation" label="Conversation behaviour">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={lbl}>Who speaks first</label>
                    <select value={s.agentSpeaksFirst ? 'agent' : 'caller'} onChange={e => set('agentSpeaksFirst', e.target.value === 'agent')} className={input}>
                      <option value="agent">Agent (plays greeting)</option>
                      <option value="caller">Caller (greeting suppressed)</option>
                    </select>
                  </div>
                  <div>
                    <label className={lbl}>Words before interruption</label>
                    <input type="number" min={0} value={s.numberOfWordsForInterruption} onChange={e => set('numberOfWordsForInterruption', e.target.value === '' ? '' : parseInt(e.target.value))} className={input} placeholder="0 = instant barge-in" />
                  </div>
                  <div>
                    <label className={lbl}>Check if caller online</label>
                    <select value={s.checkIfUserOnline} onChange={e => set('checkIfUserOnline', e.target.value as any)} className={input}>
                      <option value="">Default</option><option value="true">On</option><option value="false">Off</option>
                    </select>
                  </div>
                  <div>
                    <label className={lbl}>Trigger after silence (sec)</label>
                    <input type="number" min={0} value={s.triggerUserOnlineMessageAfter} onChange={e => set('triggerUserOnlineMessageAfter', e.target.value === '' ? '' : parseInt(e.target.value))} className={input} placeholder="e.g. 10" />
                  </div>
                  <div className="md:col-span-2">
                    <label className={lbl}>"Are you still there?" message</label>
                    <input type="text" value={s.checkUserOnlineMessage} onChange={e => set('checkUserOnlineMessage', e.target.value)} className={input} placeholder="Hello, are you still there?" />
                  </div>
                  <div>
                    <label className={lbl}>Backchanneling</label>
                    <select value={s.backchanneling} onChange={e => set('backchanneling', e.target.value as any)} className={input}>
                      <option value="">Default</option><option value="true">On</option><option value="false">Off</option>
                    </select>
                  </div>
                  <div>
                    <label className={lbl}>Ambient noise</label>
                    <select value={s.ambientNoise} onChange={e => set('ambientNoise', e.target.value as any)} className={input}>
                      <option value="">Default</option><option value="true">On</option><option value="false">Off</option>
                    </select>
                  </div>
                  {s.ambientNoise === 'true' && (
                    <div className="md:col-span-2">
                      <label className={lbl}>Ambient noise track</label>
                      <input type="text" value={s.ambientNoiseTrack} onChange={e => set('ambientNoiseTrack', e.target.value)} className={input} placeholder="e.g. office-ambience" />
                    </div>
                  )}
                  <div>
                    <label className={lbl}>Hang up after silence (sec)</label>
                    <input type="number" min={0} value={s.hangupAfterSilence} onChange={e => set('hangupAfterSilence', e.target.value === '' ? '' : parseInt(e.target.value))} className={input} placeholder="e.g. 30" />
                  </div>
                  <div>
                    <label className={lbl}>Call terminate after (sec)</label>
                    <input type="number" min={0} value={s.callTerminate} onChange={e => set('callTerminate', e.target.value === '' ? '' : parseInt(e.target.value))} className={input} placeholder="e.g. 600" />
                  </div>
                  <div className="md:col-span-2">
                    <label className={lbl}>Conversation config override (JSON)</label>
                    <textarea value={s.conversationConfigJson} onChange={e => set('conversationConfigJson', e.target.value)} rows={3} className={input + ' font-mono text-xs'} placeholder='{"optimize_latency": true}' />
                  </div>
                </div>
              </Advanced>
            </div>
          )}

          {step.id === 'capabilities' && (
            <div>
              <h3 className={h3}>Capabilities</h3>
              <p className="text-xs mb-1" style={{ color: 'var(--text-muted)' }}>
                An agent is its capability set. Each one you switch on is saved as a tool the agent can call.
              </p>
              <div className="mt-4">
                {CAPABILITIES.map(c => (
                  <div key={c.id} className="flex items-center gap-3 py-3" style={{ borderBottom: '1px solid var(--divider)' }}>
                    <div className="flex-1">
                      <div className="text-[13px] font-medium text-gray-900 dark:text-white flex items-center gap-2">
                        {c.label}
                        {c.needsWiring && s.capabilities[c.id] && (
                          <span className="tl-pill tl-pill-amber">Needs endpoint</span>
                        )}
                      </div>
                      <div className="text-[11.5px]" style={{ color: 'var(--text-muted)' }}>{c.description}</div>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={!!s.capabilities[c.id]}
                      aria-label={c.label}
                      onClick={() => set('capabilities', { ...s.capabilities, [c.id]: !s.capabilities[c.id] })}
                      className="relative w-11 h-6 rounded-full transition flex-shrink-0"
                      style={{ background: s.capabilities[c.id] ? 'var(--toggle-on)' : 'var(--gray-bg)' }}
                    >
                      <span
                        className="absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all"
                        style={{ left: s.capabilities[c.id] ? 22 : 2 }}
                      />
                    </button>
                  </div>
                ))}
              </div>
              {needsWiring.length > 0 && (
                <p className="text-xs mt-4 px-3 py-2 rounded-xl flex items-start gap-2" style={{ background: 'var(--amber-tint)', color: 'var(--amber-label)' }}>
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>
                    {needsWiring.map(c => c.label).join(', ')} {needsWiring.length === 1 ? 'calls' : 'call'} your systems.
                    The {needsWiring.length === 1 ? 'tool is' : 'tools are'} created here, but you need to point {needsWiring.length === 1 ? 'it' : 'them'} at
                    an endpoint in the agent editor before {needsWiring.length === 1 ? 'it works' : 'they work'}.
                  </span>
                </p>
              )}

              {/* Developer mode: free-form JSON tools, on top of the predefined set above. */}
              {developerMode && (
                <div className="mt-6 pt-4" style={{ borderTop: '1px solid var(--divider)' }}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[13px] font-semibold text-gray-900 dark:text-white">Custom tools</div>
                    {!toolDraft && (
                      <button type="button" className="tl-btn tl-btn-secondary tl-btn-sm"
                        onClick={() => setToolDraft({ name: '', description: '', execution_type: 'api', paramsText: '{\n  "type": "object",\n  "properties": {}\n}' })}>
                        <Wrench className="w-3.5 h-3.5" /> Add custom tool
                      </button>
                    )}
                  </div>
                  {customTools.map((ct, i) => (
                    <div key={i} className="flex items-center gap-2 py-2" style={{ borderBottom: '1px solid var(--divider)' }}>
                      <div className="flex-1">
                        <div className="text-[13px] font-medium text-gray-900 dark:text-white">{ct.name}
                          <span className="tl-pill ml-2">{ct.execution_type}</span></div>
                        <div className="text-[11.5px]" style={{ color: 'var(--text-muted)' }}>{ct.description}</div>
                      </div>
                      <button type="button" className="tl-icon-btn" onClick={() => setCustomTools(customTools.filter((_, j) => j !== i))}>
                        <AlertTriangle className="w-4 h-4" style={{ display: 'none' }} />&times;
                      </button>
                    </div>
                  ))}
                  {toolDraft && (
                    <div className="mt-3 p-3 rounded-xl" style={{ border: '1px solid var(--border)' }}>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div><label className={lbl}>Name</label>
                          <input className={input} value={toolDraft.name}
                            onChange={e => setToolDraft({ ...toolDraft, name: e.target.value })} placeholder="lookup_order" /></div>
                        <div><label className={lbl}>Execution type</label>
                          <select className={input} value={toolDraft.execution_type}
                            onChange={e => setToolDraft({ ...toolDraft, execution_type: e.target.value })}>
                            {['api','static','knowledge_base','plugin'].map(x => <option key={x} value={x}>{x}</option>)}
                          </select></div>
                        <div className="sm:col-span-2"><label className={lbl}>Description</label>
                          <input className={input} value={toolDraft.description}
                            onChange={e => setToolDraft({ ...toolDraft, description: e.target.value })} /></div>
                        <div className="sm:col-span-2"><label className={lbl}>Parameters (JSON schema)</label>
                          <textarea className={input + ' font-mono text-xs'} rows={6} value={toolDraft.paramsText}
                            onChange={e => setToolDraft({ ...toolDraft, paramsText: e.target.value })} /></div>
                      </div>
                      <div className="flex justify-end gap-2 mt-3">
                        <button type="button" className="tl-btn tl-btn-neutral tl-btn-sm" onClick={() => setToolDraft(null)}>Cancel</button>
                        <button type="button" className="tl-btn tl-btn-primary tl-btn-sm" onClick={() => {
                          if (!toolDraft.name.trim()) { setError('Tool name is required'); return; }
                          let parameters: any;
                          try { parameters = JSON.parse(toolDraft.paramsText || '{}'); }
                          catch { setError('Parameters must be valid JSON'); return; }
                          setCustomTools([...customTools, { name: toolDraft.name.trim(), description: toolDraft.description,
                            execution_type: toolDraft.execution_type as any, enabled: true, parameters, execution_config: {} }]);
                          setToolDraft(null); setError(null);
                        }}>Add</button>
                      </div>
                    </div>
                  )}
                  {!developerMode && null}
                </div>
              )}
            </div>
          )}

          {step.id === 'instructions' && (
            <div>
              <h3 className={h3}>Instructions</h3>
              <label className={lbl}>Greeting</label>
              <textarea value={s.greetingMessage} onChange={e => set('greetingMessage', e.target.value)} rows={2} className={input} placeholder="The first thing callers hear" />
              <div className="mt-4">
                <label className={lbl}>How it should behave</label>
                <textarea value={s.systemPrompt} onChange={e => set('systemPrompt', e.target.value)} rows={9} className={input} placeholder="Anything else the agent should know, or how it should handle specific situations." />
                <p className="text-xs mt-2" style={{ color: 'var(--text-muted)' }}>
                  Your chosen tone ({s.tone}) is appended to this automatically.
                </p>
              </div>
              <Advanced id="brain" label="Model &amp; memory">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={lbl}>Memory profile</label>
                    <select value={s.memoryProfileId} onChange={e => set('memoryProfileId', e.target.value === '' ? '' : Number(e.target.value))} className={input}>
                      <option value="">None</option>
                      {memories.map(m => <option key={m.id} value={m.id}>{m.name} ({m.provider})</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={lbl}>LLM provider</label>
                    <select value={s.provider} onChange={e => set('provider', e.target.value)} className={input}>
                      {llmProviderOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={lbl}>Model</label>
                    {/* Catalog-driven when the catalog knows models for this provider;
                        a free-text input otherwise, so a custom model is never blocked. */}
                    {llmModelOptions.length > 0 ? (
                      <select value={s.model} onChange={e => set('model', e.target.value)} className={input}>
                        {llmModelOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    ) : (
                      <input type="text" value={s.model} onChange={e => set('model', e.target.value)} className={input} placeholder={isRealtime ? REALTIME_MODEL : DEFAULT_MODEL} />
                    )}
                  </div>
                  <div>
                    <label className={lbl}>Agent flow type</label>
                    {isRealtime ? (
                      // For a realtime agent the flow type IS the realtime
                      // marker: the backend decides the whole shape of the
                      // document from it (AiAgentService.isRealtimeFlow), so
                      // both options this select could offer would turn the
                      // agent back into an STT→LLM→TTS pipeline. A control
                      // whose every option is wrong is worse than no control.
                      <div className="px-4 py-2.5 rounded-xl text-sm" style={{ background: 'var(--surface-raised)', color: 'var(--text-muted)' }}>
                        Realtime (voice-to-voice) — set by the runtime type
                      </div>
                    ) : (
                      <select value={s.agentFlowType} onChange={e => set('agentFlowType', e.target.value)} className={input}>
                        <option value="streaming">Streaming</option>
                        <option value="non_streaming">Non-streaming</option>
                      </select>
                    )}
                  </div>
                  <div>
                    <label className={lbl}>Temperature ({s.temperature})</label>
                    <input type="range" min="0" max="1" step="0.1" value={s.temperature} onChange={e => set('temperature', parseFloat(e.target.value))} className="w-full" />
                  </div>
                  <div>
                    <label className={lbl}>Max response tokens</label>
                    <input type="number" min={50} max={2000} value={s.maxTokens} onChange={e => set('maxTokens', parseInt(e.target.value))} className={input} />
                  </div>
                </div>
              </Advanced>
            </div>
          )}

          {step.id === 'review' && (
            <div>
              <h3 className={h3}>Review &amp; activate</h3>
              <dl className="text-[13px]">
                {([
                  ['Name', s.name || '—'],
                  ['Template', template?.label || '—'],
                  ['Runtime', s.modality === 'text' ? 'Text / messaging' : s.modality === 'voice_realtime' ? 'Voice — realtime' : 'Voice — pipeline'],
                  ...(isVoice ? [
                    ['Language', LANGUAGES.find(l => l.code === s.language)?.label || s.language],
                    ['Voice', `${voiceCards.find(v => v.id === s.voice)?.name || s.voice} · ${s.tone}`],
                  ] as [string, string][] : []),
                  ['Capabilities', enabledCaps.length ? enabledCaps.map(c => c.label).join(', ') : 'None'],
                  ['Knowledge', s.knowledgeBase.trim() ? `${s.knowledgeBase.trim().split(/\s+/).length} words` : 'None'],
                  ['Document RAG', s.enableKnowledgeBase ? 'Enabled' : 'Off'],
                  ['Model', `${s.provider} · ${s.model}`],
                ] as [string, string][]).map(([k, v]) => (
                  <div key={k} className="flex gap-4 py-2.5" style={{ borderBottom: '1px solid var(--divider)' }}>
                    <dt className="w-36 flex-shrink-0" style={{ color: 'var(--text-muted)' }}>{k}</dt>
                    <dd className="flex-1 text-gray-900 dark:text-white">{v}</dd>
                  </div>
                ))}
              </dl>
              {needsWiring.length > 0 && (
                <p className="text-xs mt-4 px-3 py-2 rounded-xl" style={{ background: 'var(--amber-tint)', color: 'var(--amber-label)' }}>
                  {needsWiring.map(c => c.label).join(', ')} still need an endpoint before they will work.
                </p>
              )}
              <p className="text-xs mt-4" style={{ color: 'var(--text-muted)' }}>
                After creating, bind this agent to a channel (voice number, WhatsApp, RCS…) in the agent editor.
              </p>
            </div>
          )}
        </div>

        {/* Live summary */}
        <div className="rounded-2xl p-4 lg:sticky lg:top-0" style={{ background: 'var(--coral-tint)' }}>
          <div className="text-xs font-semibold mb-3" style={{ color: 'var(--coral-label)' }}>Live summary</div>
          <div className="flex items-center gap-2.5 mb-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center font-semibold" style={{ background: 'var(--surface)', color: 'var(--coral-label)' }}>
              {(s.name || '?').charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="font-semibold truncate" style={{ color: 'var(--coral-value)' }}>{s.name || 'Unnamed agent'}</div>
              <div className="text-[11.5px] truncate" style={{ color: 'var(--coral-label)' }}>
                {template?.label || 'No template'}
                {isVoice && ` · ${LANGUAGES.find(l => l.code === s.language)?.label || s.language}`}
              </div>
            </div>
          </div>
          <div className="text-[11.5px] font-semibold mb-1.5" style={{ color: 'var(--coral-label)' }}>
            Can do ({enabledCaps.length})
          </div>
          <div className="rounded-xl p-3 text-[12px] leading-relaxed" style={{ background: 'var(--surface)', color: 'var(--text-primary)' }}>
            {enabledCaps.length
              ? <ul className="space-y-1">{enabledCaps.map(c => (
                  <li key={c.id} className="flex items-start gap-1.5">
                    <Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: 'var(--toggle-on)' }} />
                    <span>{c.label}</span>
                  </li>))}</ul>
              : <span style={{ color: 'var(--text-muted)' }}>Nothing switched on yet.</span>}
          </div>
          {s.greetingMessage.trim() && (
            <>
              <div className="text-[11.5px] font-semibold mt-3 mb-1.5" style={{ color: 'var(--coral-label)' }}>Opens with</div>
              <div className="rounded-xl p-3 text-[12px] leading-relaxed" style={{ background: 'var(--surface)', color: 'var(--text-primary)' }}>
                “{s.greetingMessage}”
              </div>
            </>
          )}
        </div>
      </div>

      {/* Footer nav */}
      <div className="flex items-center justify-between lg:pr-[310px]">
        <button
          type="button"
          onClick={() => (stepIdx === 0 ? navigate(-1) : setStepIdx(stepIdx - 1))}
          className="tl-btn tl-btn-neutral"
        >
          {stepIdx === 0 ? 'Cancel' : 'Back'}
        </button>
        {isLast ? (
          <button type="button" onClick={handleSubmit} disabled={loading || !s.name.trim()} className="tl-btn tl-btn-primary">
            <Check className="w-4 h-4" />
            {loading ? 'Creating…' : 'Create agent'}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setStepIdx(stepIdx + 1)}
            disabled={!canAdvance}
            className="tl-btn tl-btn-primary"
            title={!canAdvance ? 'Pick a template and give the agent a name' : undefined}
          >
            Continue
            <ArrowRight className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

export default AIAgentCreation;
